<!--
@component Scrollable, dynamic code snippet with line numbers and highlighting.

These should be embedded in more complex UI components, not standalone. For informational
code samples, use the CodeSample component instead.
-->

<script lang="ts" context="module">
  import { browser } from "$app/environment";

  import { createHighlighter } from "shiki";
  import { tick } from "svelte";

  const highlighter = await createHighlighter({
    themes: ["dark-plus"],
    langs: ["python"],
  });
</script>

<script lang="ts">
  export let source: string;
  export let highlightedLine: number | null = null;
  export let scrollToHighlightedLine = false;
  export let fontSize: "sm" | "xs" = "sm";

  let codeContainer: HTMLElement;
  let codeElement: HTMLElement;

  $: renderedCode = highlighter.codeToHtml(source, {
    lang: "python",
    theme: "dark-plus",
  });

  async function onUpdateCode() {
    // div > pre > code > span.line{*}
    await tick();
    const preElement = codeContainer.querySelector("pre.shiki");
    if (preElement && preElement instanceof HTMLElement) {
      // Remove the background color set by shiki. This allows the parent component
      // to set a background.
      preElement.style.backgroundColor = "";
      codeElement = preElement.children[0] as HTMLElement;
    }
  }

  $: renderedCode, browser && onUpdateCode();

  $: if (codeElement && highlightedLine) {
    const lineElements = codeElement.children;

    const highlightedElements = codeElement.querySelectorAll(".highlighted");
    highlightedElements.forEach((element) => {
      element.classList.remove("highlighted");
    });

    if (highlightedLine >= 1 && highlightedLine <= lineElements.length) {
      const lineElement = lineElements[highlightedLine - 1] as HTMLElement;
      lineElement.classList.add("highlighted");
      if (scrollToHighlightedLine) {
        lineElement.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    }
  }
</script>

<div
  class:text-sm={fontSize === "sm"}
  class:text-xs={fontSize === "xs"}
  bind:this={codeContainer}
>
  {@html renderedCode}
</div>

<style lang="postcss">
  div {
    @apply h-full overflow-scroll;
  }

  div :global {
    pre {
      @apply py-2 w-full font-mono leading-normal whitespace-pre-wrap break-all;
    }

    ::selection {
      @apply bg-white/10;
    }

    code {
      @apply block;
      counter-reset: step;
      counter-increment: step 0;
    }

    code .line {
      @apply inline-block w-full;
      text-indent: -48px;
      padding-left: 48px;
    }

    code .line.highlighted {
      @apply bg-light-green/10;
    }

    code .line::before {
      content: counter(step);
      counter-increment: step;
      @apply mr-4 w-8 tabular-nums text-white/30;
      display: inline-block;
      text-align: right;
    }
  }
</style>
